/**
 * Loader autónomo legacy: QIDO-RS (series/instancias) + WADO-URI (bytes).
 * Sin manifest R2 ni Orthanc REST.
 */
import { groupBy } from '../../functions'
import { configureWadoLoaderAuth } from '../wadoAuth'
import {
  loadDicomInstanceWithCache,
  fallbackCacheKey,
} from '../loadDicomInstanceWithCache'
import { configureWadoLoaderForManifestAndOrthanc } from '../loadStudyFromManifest'
import { getManifestDownloadConcurrency } from '../mobileDownloadProfile'
import { isScoutSeries } from '../isScoutSeries'
import {
  isStudyLoadCancelled,
  throwIfStudyLoadCancelled,
  createStudyLoadAbortError,
  STUDY_LOAD_SOURCE,
} from '../studyLoadSource'
import {
  buildSeriesSlots,
  createSeriesPriorityScheduler,
  createDownloadProgressTracker,
  estimateLoadedBytes,
} from '../downloadSeriesScheduler'

/** Nombre de paciente inventado por el skeleton; no es un PN DICOM. */
export const LEGACY_WADO_PLACEHOLDER_NAME = 'Legacy WADO'

export function emptyStudyMeta() {
  return {
    patientName: '',
    patientId: '',
    studyDate: '',
    studyTime: '',
    studyDescription: '',
  }
}

/** Suma NumberOfSeriesRelatedInstances (00201209) del listado QIDO. */
export function sumRelatedInstances(seriesList) {
  if (!Array.isArray(seriesList)) return 0
  let n = 0
  for (const s of seriesList) {
    const v = s && Number(s.relatedInstances)
    if (Number.isFinite(v) && v > 0) n += Math.floor(v)
  }
  return n
}

/** Host:puerto del WADO/QIDO (IP LAN o FQDN). */
export function originHostFromUrl(url) {
  const raw = url != null ? String(url).trim() : ''
  if (!raw) return ''
  try {
    return new URL(raw).host || ''
  } catch (e) {
    return ''
  }
}

export function seriesInfoFromQidoList(seriesList) {
  if (!Array.isArray(seriesList)) return []
  return seriesList.map((s, idx) => ({
    seriesNumber: (s && parseInt(s.seriesNumber, 10)) || idx + 1,
    description: String(
      (s && (s.description || s.seriesDescription)) || ''
    ).trim(),
    totalTasks:
      s && s.relatedInstances > 0
        ? s.relatedInstances
        : (s && s.instances && s.instances.length) || 1,
  }))
}

/**
 * PN DICOM (string o { Alphabetic }) → texto usable en explorer / barra.
 * @param {*} v
 * @returns {string|null}
 */
export function stringifyQidoValue(v) {
  if (v == null) return null
  if (typeof v === 'string' || typeof v === 'number') {
    const s = String(v).trim()
    return s || null
  }
  if (typeof v === 'object') {
    const pn =
      v.Alphabetic ||
      v.alphabetic ||
      v.Ideographic ||
      v.ideographic ||
      v.Phonetic ||
      v.phonetic
    if (pn != null) {
      const s = String(pn).trim()
      return s || null
    }
  }
  return null
}

export function isPlaceholderPatientName(name) {
  const s = String(name || '').trim()
  return !s || s === LEGACY_WADO_PLACEHOLDER_NAME
}

/** @param {object} item @param {string} tag e.g. '0020000E' */
export function qidoTagValue(item, tag) {
  if (!item || typeof item !== 'object') return null
  const entry = item[tag]
  if (!entry || !Array.isArray(entry.Value) || entry.Value.length === 0) {
    return null
  }
  return stringifyQidoValue(entry.Value[0])
}

export function studyMetaFromQidoRow(row) {
  if (!row || typeof row !== 'object') return emptyStudyMeta()
  return {
    patientName: qidoTagValue(row, '00100010') || '',
    patientId: qidoTagValue(row, '00100020') || '',
    studyDate: qidoTagValue(row, '00080020') || '',
    studyTime: qidoTagValue(row, '00080030') || '',
    studyDescription: qidoTagValue(row, '00081030') || '',
  }
}

export function mergeStudyMeta(base, extra) {
  const a = base && typeof base === 'object' ? base : emptyStudyMeta()
  const b = extra && typeof extra === 'object' ? extra : emptyStudyMeta()
  return {
    patientName: a.patientName || b.patientName || '',
    patientId: a.patientId || b.patientId || '',
    studyDate: a.studyDate || b.studyDate || '',
    studyTime: a.studyTime || b.studyTime || '',
    studyDescription: a.studyDescription || b.studyDescription || '',
  }
}

export function studyMetaFromHint(ctx) {
  if (!ctx || typeof ctx !== 'object') return emptyStudyMeta()
  return {
    patientName: String(ctx.patientName || ctx.patient_name || '').trim(),
    patientId: String(ctx.patientId || ctx.patientID || ctx.patient_id || '').trim(),
    studyDate: String(ctx.studyDate || ctx.study_date || '').trim(),
    studyTime: String(ctx.studyTime || ctx.study_time || '').trim(),
    studyDescription: String(
      ctx.studyDescription || ctx.study_description || ''
    ).trim(),
  }
}

/**
 * QIDO-RS a nivel estudio (PatientName / StudyDescription).
 * No lanza: si el PACS no lo soporta, el caller usa series/instancias o tags DICOM.
 */
export async function fetchStudyQidoMeta(ctx) {
  const studyUID = ctx && ctx.studyUID
  const qidoBase = ctx && ctx.qidoBase
  if (!studyUID || !qidoBase) return emptyStudyMeta()
  const authHeaders = (ctx && ctx.authHeaders) || {}
  try {
    const path = `studies?StudyInstanceUID=${encodeURIComponent(studyUID)}`
    const rows = await qidoFetchJson(qidoBase, path, authHeaders)
    if (Array.isArray(rows) && rows[0]) {
      return studyMetaFromQidoRow(rows[0])
    }
  } catch (e) {
    /* algunos Orthanc responden 404 al query; se intenta Retrieve */
  }
  try {
    const path = `studies/${encodeURIComponent(studyUID)}`
    const rows = await qidoFetchJson(qidoBase, path, authHeaders)
    if (Array.isArray(rows) && rows[0]) {
      return studyMetaFromQidoRow(rows[0])
    }
  } catch (e) {
    /* meta queda vacía; overlay DICOM al descargar instancias */
  }
  return emptyStudyMeta()
}

export function dicomString(image, tag) {
  try {
    if (image && image.data && typeof image.data.string === 'function') {
      const v = image.data.string(tag)
      return v != null ? String(v).trim() : ''
    }
  } catch (e) {
    return ''
  }
  return ''
}

export function studyMetaFromDicomImage(image) {
  if (!image) return emptyStudyMeta()
  return {
    patientName: dicomString(image, 'x00100010'),
    patientId: dicomString(image, 'x00100020'),
    studyDate: dicomString(image, 'x00080020'),
    studyTime: dicomString(image, 'x00080030'),
    studyDescription: dicomString(image, 'x00081030'),
  }
}

export function applyStudyMetaToItem(item, meta) {
  if (!item) return item
  const m = mergeStudyMeta(emptyStudyMeta(), meta)
  const curName = item.patient && item.patient.patientName
  const curId =
    (item.patient && (item.patient.patientId || item.patient.patientID)) || ''
  const study = item.study || {}
  const date = study.studyDate || m.studyDate || ''
  const time = study.studyTime || m.studyTime || ''
  return {
    ...item,
    patient: {
      patientName: isPlaceholderPatientName(curName)
        ? m.patientName || ''
        : curName,
      patientId: String(curId).trim() || m.patientId || '',
    },
    study: {
      ...study,
      studyDate: date,
      studyTime: time,
      studyDateTime:
        study.studyDateTime || [date, time].filter(Boolean).join('.') || '',
      studyDescription: study.studyDescription || m.studyDescription || '',
    },
  }
}

export function applyStudyMetaToItems(items, meta) {
  if (!Array.isArray(items) || !meta) return items
  if (
    isPlaceholderPatientName(meta.patientName) &&
    !meta.patientId &&
    !meta.studyDescription &&
    !meta.studyDate
  ) {
    return items
  }
  for (let i = 0; i < items.length; i++) {
    items[i] = applyStudyMetaToItem(items[i], meta)
  }
  return items
}

/**
 * @param {string} qidoBase
 * @param {string} path — relativo a qidoBase (sin leading slash; puede llevar query)
 * @param {Record<string, string>} authHeaders
 */
async function qidoFetchJson(qidoBase, path, authHeaders = {}) {
  const base = String(qidoBase || '').replace(/\/$/, '')
  const rel = String(path || '').replace(/^\//, '')
  const url = `${base}/${rel}`
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/dicom+json',
      ...authHeaders,
    },
    credentials: 'omit',
  })
  if (!res.ok) {
    throw new Error(`QIDO-RS ${url} → HTTP ${res.status}`)
  }
  const json = await res.json()
  return Array.isArray(json) ? json : []
}

/**
 * @param {object} row
 * @returns {{ sopInstanceUID: string, instanceNumber: number }|null}
 */
function mapQidoInstanceRow(row) {
  const sopInstanceUID = qidoTagValue(row, '00080018') || ''
  if (!sopInstanceUID) return null
  return {
    sopInstanceUID,
    instanceNumber: parseInt(qidoTagValue(row, '00200013') || '0', 10) || 0,
  }
}

/**
 * @param {object[]} instJson
 * @returns {Array<{ sopInstanceUID: string, instanceNumber: number }>}
 */
export function parseQidoInstances(instJson) {
  return (Array.isArray(instJson) ? instJson : [])
    .map(mapQidoInstanceRow)
    .filter(Boolean)
    .sort((a, b) => a.instanceNumber - b.instanceNumber)
}

/**
 * @param {string} studyUID
 * @param {string} seriesUID
 * @param {{ limit?: number }} [opts]
 * @returns {string}
 */
export function buildQidoInstancesPath(studyUID, seriesUID, opts = {}) {
  let path = `studies/${encodeURIComponent(studyUID)}/series/${encodeURIComponent(seriesUID)}/instances`
  const q = []
  const limit = Number(opts.limit)
  if (Number.isFinite(limit) && limit > 0) {
    q.push(`limit=${Math.floor(limit)}`)
  }
  const offset = Number(opts.offset)
  if (Number.isFinite(offset) && offset > 0) {
    q.push(`offset=${Math.floor(offset)}`)
  }
  if (q.length) path += `?${q.join('&')}`
  return path
}

/** Primera tanda WADO: bastante para scroll, sin esperar el QIDO completo. */
export const FIRST_SERIES_QIDO_PAGE = 48

export function mergeQidoInstances(a, b) {
  const seen = Object.create(null)
  const out = []
  const lists = [a, b]
  for (let i = 0; i < lists.length; i++) {
    const list = Array.isArray(lists[i]) ? lists[i] : []
    for (let j = 0; j < list.length; j++) {
      const inst = list[j]
      const sop = inst && inst.sopInstanceUID
      if (!sop) continue
      const k = String(sop)
      if (seen[k]) continue
      seen[k] = true
      out.push(inst)
    }
  }
  out.sort(
    (x, y) => (Number(x.instanceNumber) || 0) - (Number(y.instanceNumber) || 0)
  )
  return out
}

/**
 * Lista de series QIDO (sin instancias).
 * @param {{ qidoBase: string, studyUID: string, seriesUID?: string|null, authHeaders?: object }} ctx
 */
export async function fetchQidoSeriesList(ctx) {
  const studyUID = ctx && ctx.studyUID
  const qidoBase = ctx && ctx.qidoBase
  if (!studyUID || !qidoBase) {
    throw new Error('fetchQidoSeriesList: qidoBase y studyUID requeridos')
  }
  const authHeaders = (ctx && ctx.authHeaders) || {}
  const seriesPath = `studies/${encodeURIComponent(studyUID)}/series`
  const seriesJson = await qidoFetchJson(qidoBase, seriesPath, authHeaders)
  let qidoStudyMeta = mergeStudyMeta(emptyStudyMeta(), studyMetaFromQidoRow(seriesJson[0]))
  let seriesList = seriesJson.map((row) => ({
    seriesInstanceUID: qidoTagValue(row, '0020000E') || '',
    seriesNumber: parseInt(qidoTagValue(row, '00200011') || '0', 10) || 0,
    modality: qidoTagValue(row, '00080060') || '',
    description: qidoTagValue(row, '0008103E') || '',
    relatedInstances: parseInt(qidoTagValue(row, '00201209') || '0', 10) || 0,
    instances: [],
  }))
  seriesList = seriesList.filter((s) => s.seriesInstanceUID)
  if (ctx.seriesUID) {
    const filterUid = String(ctx.seriesUID).trim()
    seriesList = seriesList.filter((s) => s.seriesInstanceUID === filterUid)
  }
  return { seriesList, qidoStudyMeta }
}

/**
 * Instancias QIDO de una serie. `limit` acelera first-paint (visorweb ~0.8 s vs 2.5 s).
 * @param {{ qidoBase: string, studyUID: string, authHeaders?: object }} ctx
 * @param {string} seriesUID
 * @param {{ limit?: number }} [opts]
 */
export async function fetchQidoSeriesInstances(ctx, seriesUID, opts = {}) {
  const studyUID = ctx && ctx.studyUID
  const qidoBase = ctx && ctx.qidoBase
  const uid = String(seriesUID || '').trim()
  if (!studyUID || !qidoBase || !uid) {
    return { instances: [], qidoStudyMeta: emptyStudyMeta() }
  }
  const authHeaders = (ctx && ctx.authHeaders) || {}
  const instJson = await qidoFetchJson(
    qidoBase,
    buildQidoInstancesPath(studyUID, uid, opts),
    authHeaders
  )
  return {
    instances: parseQidoInstances(instJson),
    qidoStudyMeta: mergeStudyMeta(emptyStudyMeta(), studyMetaFromQidoRow(instJson[0])),
  }
}

/**
 * @param {Array} items
 * @param {number} concurrency
 * @param {(item: any, idx: number) => Promise<any>} fn
 */
async function mapPool(items, concurrency, fn) {
  const list = Array.isArray(items) ? items : []
  const ret = new Array(list.length)
  let next = 0
  const n = Math.max(1, Number(concurrency) || 1)
  async function worker() {
    while (next < list.length) {
      const i = next
      next += 1
      ret[i] = await fn(list[i], i)
    }
  }
  const runners = []
  for (let i = 0; i < Math.min(n, list.length); i++) runners.push(worker())
  await Promise.all(runners)
  return ret
}

/**
 * Índice de serie para extras: mismo orden que el scheduler de first-paint
 * (`structure`), no el índice crudo de QIDO (puede haber series sin instancias).
 * @param {object} series
 * @param {Array<{ seriesInstanceUID?: string }>} structure
 * @param {object[]} listedSeries
 * @returns {number}
 */
export function resolveWadoExtraSeriesIdx(series, structure, listedSeries) {
  const uid = series && series.seriesInstanceUID
    ? String(series.seriesInstanceUID)
    : ''
  if (uid && Array.isArray(structure)) {
    const fromStructure = structure.findIndex(
      (s) => s && String(s.seriesInstanceUID || '') === uid
    )
    if (fromStructure >= 0) return fromStructure
  }
  const fromListed = Array.isArray(listedSeries)
    ? listedSeries.indexOf(series)
    : -1
  return fromListed >= 0 ? fromListed : 0
}

/**
 * Pool WADO con prioridad de serie activa (misma política que R2 / fase 1).
 * No aborta slots en vuelo: solo cambia el próximo pick.
 *
 * @param {{
 *   tasks: Array<{ seriesIdx: number }>,
 *   seriesInfo?: Array<{ totalTasks?: number }>,
 *   parallel?: number,
 *   getActiveSeriesIdx?: (() => number)|null,
 *   getPinnedSeriesIdx?: (() => number[])|null,
 *   cancelled?: () => boolean,
 *   onSlot: (slot: number, task: object) => Promise<void>,
 * }} opts
 */
export async function runPriorityWadoPool(opts) {
  const tasks = Array.isArray(opts && opts.tasks) ? opts.tasks : []
  if (!tasks.length) return
  const seriesInfo = Array.isArray(opts.seriesInfo) ? opts.seriesInfo : []
  const seriesSlots = buildSeriesSlots(tasks)
  const maxTaskIdx = tasks.reduce((m, t) => {
    const n = t && typeof t.seriesIdx === 'number' ? t.seriesIdx : 0
    return Math.max(m, n + 1)
  }, 0)
  const numSeries = Math.max(seriesInfo.length, maxTaskIdx, 1)
  const parallel = Math.max(1, Number(opts.parallel) || 1)
  const cancelled =
    typeof opts.cancelled === 'function' ? opts.cancelled : () => false
  const onSlot = typeof opts.onSlot === 'function' ? opts.onSlot : async () => {}

  const scheduler = createSeriesPriorityScheduler({
    numSeries,
    seriesSlots,
    seriesInfo,
    parallel,
    getActiveSeriesIdx:
      typeof opts.getActiveSeriesIdx === 'function'
        ? opts.getActiveSeriesIdx
        : () => 0,
    getPinnedSeriesIdx:
      typeof opts.getPinnedSeriesIdx === 'function'
        ? opts.getPinnedSeriesIdx
        : () => [],
  })

  async function worker() {
    while (!scheduler.isDone() && scheduler.completed.size < tasks.length) {
      if (cancelled()) return
      const slot = scheduler.pickNextSlot()
      if (slot == null) {
        if (scheduler.completed.size >= tasks.length) return
        await new Promise((r) => setTimeout(r, 2))
        continue
      }
      try {
        await onSlot(slot, tasks[slot])
      } finally {
        scheduler.finishSlot(slot)
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(parallel, tasks.length) }, () => worker())
  )
}

/**
 * Descubre series e instancias del estudio vía QIDO-RS.
 *
 * @param {{
 *   qidoBase: string,
 *   studyUID: string,
 *   seriesUID?: string|null,
 *   authHeaders?: Record<string, string>,
 * }} ctx
 * @returns {Promise<Array<{
 *   seriesInstanceUID: string,
 *   seriesNumber: number,
 *   modality: string,
 *   description: string,
 *   instances: Array<{ sopInstanceUID: string, instanceNumber: number }>,
 * }>>}
 */
export async function discoverStudyStructure(ctx) {
  const listed = await fetchQidoSeriesList(ctx)
  let qidoStudyMeta = listed.qidoStudyMeta
  const seriesList = listed.seriesList
  for (const series of seriesList) {
    const got = await fetchQidoSeriesInstances(ctx, series.seriesInstanceUID)
    qidoStudyMeta = mergeStudyMeta(qidoStudyMeta, got.qidoStudyMeta)
    series.instances = got.instances
  }
  const seriesWithInstances = seriesList.filter((s) => s.instances.length > 0)
  seriesWithInstances.qidoStudyMeta = qidoStudyMeta
  return seriesWithInstances
}

/**
 * URL WADO-URI clásica por instancia.
 */
export function buildWadoInstanceUrl(wadoUrl, studyUID, seriesUID, sopInstanceUID) {
  const base = String(wadoUrl || '').replace(/\/$/, '')
  const params = new URLSearchParams({
    requestType: 'WADO',
    studyUID: String(studyUID),
    seriesUID: String(seriesUID),
    objectUID: String(sopInstanceUID),
    contentType: 'application/dicom',
  })
  return `${base}?${params.toString()}`
}

function buildExplorerData(items) {
  const patientList = groupBy(items, (a) => a.patient.patientName)
  return {
    folder: null,
    patient: {
      list: patientList,
      keys: [...patientList.keys()],
    },
  }
}

function buildSkeletonItem({
  studyUID,
  series,
  inst,
  slot,
  wadoUrl,
  totalImagesInSeries,
  studyMeta,
}) {
  const absoluteUrl = buildWadoInstanceUrl(
    wadoUrl,
    studyUID,
    series.seriesInstanceUID,
    inst.sopInstanceUID
  )
  const imageId = `wadouri:${absoluteUrl}`
  const instanceNumber = inst.instanceNumber || slot + 1
  const seriesNumber = series.seriesNumber || 0

  return {
    imageId,
    instanceNumber,
    name: `${instanceNumber}.dcm`,
    image: null,
    sopInstanceUID: inst.sopInstanceUID,
    seriesInstanceUID: series.seriesInstanceUID,
    dicomUrl: absoluteUrl,
    previewUrl: null,
    archiveUrl: null,
    byteOffset: null,
    byteLength: null,
    dicomFetchType: null,
    renderKind: 'dicom',
    usedFallback: false,
    skeleton: true,
    patient: {
      patientName: (studyMeta && studyMeta.patientName) || '',
      patientId: (studyMeta && studyMeta.patientId) || '',
    },
    study: {
      studyId: studyUID,
      studyDate: (studyMeta && studyMeta.studyDate) || '',
      studyTime: (studyMeta && studyMeta.studyTime) || '',
      studyDateTime:
        studyMeta && (studyMeta.studyDate || studyMeta.studyTime)
          ? [studyMeta.studyDate, studyMeta.studyTime].filter(Boolean).join('.')
          : '',
      studyDescription: (studyMeta && studyMeta.studyDescription) || '',
    },
    series: {
      seriesDate: '',
      seriesTime: '',
      seriesDescription: series.description || '',
      seriesNumber,
      seriesInstanceUID: series.seriesInstanceUID,
      echoNumber: 0,
      totalImages: totalImagesInSeries,
      modality: series.modality || '',
      isScout: isScoutSeries({
        modality: series.modality || '',
        seriesDescription: series.description || '',
        instanceCount: totalImagesInSeries,
      }),
    },
    fromCache: false,
  }
}

/**
 * Carga estudio legacy: QIDO discovery → skeleton Redux → WADO-URI downloads.
 *
 * @param {{
 *   wadoUrl: string,
 *   studyUID: string,
 *   seriesUID?: string|null,
 *   qidoBase: string,
 *   authHeaders?: Record<string, string>,
 * }} ctx
 * @param {object} handlers — mismo contrato que loadStudyFromManifest
 */
export async function loadStudyViaLegacyWado(ctx, handlers = {}) {
  const {
    setFilesStore,
    onExplorerUpdate,
    isOpenStore,
    activeDcmIndex = 0,
    concurrency = 0,
    orthancCredentials,
    onDownloadProgress,
    getActiveSeriesIdx,
    getPinnedSeriesIdx,
    signal,
    isCancelled,
    skipIdbWrite = false,
    skipIdbRead = false,
  } = handlers

  const cancelled = () => isStudyLoadCancelled(signal, isCancelled)
  const assertNotCancelled = () => throwIfStudyLoadCancelled(signal, isCancelled)

  if (!ctx || !ctx.wadoUrl || !ctx.studyUID || !ctx.qidoBase) {
    throw new Error('loadStudyViaLegacyWado: contexto incompleto')
  }

  const authHeaders = ctx.authHeaders || {}
  configureWadoLoaderForManifestAndOrthanc(orthancCredentials || null)
  configureWadoLoaderAuth(orthancCredentials || null)

  const qidoCtx = {
    qidoBase: ctx.qidoBase,
    studyUID: ctx.studyUID,
    seriesUID: ctx.seriesUID,
    authHeaders,
  }

  const studyMetaPromise = fetchStudyQidoMeta(qidoCtx)
  const listed = await fetchQidoSeriesList(qidoCtx)
  if (!listed.seriesList.length) {
    throw new Error('QIDO-RS no devolvió series con instancias')
  }

  const seriesInfo = seriesInfoFromQidoList(listed.seriesList)
  const expectedHint = sumRelatedInstances(listed.seriesList)
  const originHost = originHostFromUrl(ctx.wadoUrl || ctx.qidoBase)
  const progressTracker = createDownloadProgressTracker({
    totalTasks: expectedHint || listed.seriesList.length,
    expectedTotal: expectedHint,
    catalogComplete: false,
    seriesInfo,
    loadSource: STUDY_LOAD_SOURCE.LEGACY_WADO,
    onProgress: onDownloadProgress,
    throttleMs: 220,
    extraStats: () => ({
      studyInstanceUID: ctx.studyUID,
      originHost: ctx.originHost || originHost,
      originUrl: ctx.wadoUrl || ctx.qidoBase || '',
      lanHost: ctx.lanHost || '',
      originScope: ctx.originScope || '',
      lanSkipReason: ctx.lanSkipReason || '',
    }),
  })
  progressTracker.emit(true)

  const countedSops = Object.create(null)
  function noteDownloadProgress(sop, seriesIdx, result) {
    const k = String(sop || '')
    if (k && countedSops[k]) return
    if (k) countedSops[k] = true
    progressTracker.recordTaskDone({
      seriesIdx: typeof seriesIdx === 'number' ? seriesIdx : 0,
      ok: !!(result && result.ok),
      bytes: (result && result.bytes) || 0,
    })
    progressTracker.emit(false)
  }

  function syncSeriesInfoTotals() {
    listed.seriesList.forEach((series, idx) => {
      if (!seriesInfo[idx]) return
      const n =
        (series.instances && series.instances.length) ||
        series.relatedInstances ||
        0
      if (n > (seriesInfo[idx].totalTasks || 0)) {
        seriesInfo[idx].totalTasks = n
      }
    })
  }

  const firstSeries = listed.seriesList[0]
  let firstGot = { instances: [], qidoStudyMeta: emptyStudyMeta() }
  try {
    firstGot = await fetchQidoSeriesInstances(
      qidoCtx,
      firstSeries.seriesInstanceUID,
      { limit: 1 }
    )
  } catch (eFirst) {
    console.warn('[wadoUriLoader] QIDO first instance falló, full discover', eFirst)
  }

  if (!firstGot.instances.length) {
    const structureFallback = await discoverStudyStructure(qidoCtx)
    if (!structureFallback.length) {
      throw new Error('QIDO-RS no devolvió series con instancias')
    }
    listed.seriesList = structureFallback
    listed.qidoStudyMeta = mergeStudyMeta(
      listed.qidoStudyMeta,
      structureFallback.qidoStudyMeta
    )
    firstGot.instances = structureFallback[0].instances.slice(0, 1)
    firstGot.qidoStudyMeta = structureFallback.qidoStudyMeta
    seriesInfo.length = 0
    seriesInfoFromQidoList(listed.seriesList).forEach((row) => seriesInfo.push(row))
    const fallbackExpected = sumRelatedInstances(listed.seriesList)
    progressTracker.setExpectedTotal(
      fallbackExpected ||
        listed.seriesList.reduce(
          (n, s) => n + ((s.instances && s.instances.length) || 0),
          0
        )
    )
    progressTracker.emit(true)
  }

  let studyMeta = mergeStudyMeta(
    mergeStudyMeta(studyMetaFromHint(ctx), listed.qidoStudyMeta),
    firstGot.qidoStudyMeta
  )

  const firstSeriesPaint = {
    ...firstSeries,
    instances: firstGot.instances,
  }
  const firstHintTotal =
    firstSeries.relatedInstances > 0
      ? firstSeries.relatedInstances
      : firstGot.instances.length

  const firstItem = buildSkeletonItem({
    studyUID: ctx.studyUID,
    series: firstSeriesPaint,
    inst: firstGot.instances[0],
    slot: 0,
    wadoUrl: ctx.wadoUrl,
    totalImagesInSeries: firstHintTotal,
    studyMeta,
  })
  let orderedItems = [firstItem]
  setFilesStore([...orderedItems])
  onExplorerUpdate(buildExplorerData(orderedItems))
  isOpenStore({ index: activeDcmIndex, value: true })

  const loadedSops = Object.create(null)

  async function fillLoadedItem(slot, stub, task) {
    const loaded = await loadDicomInstanceWithCache({
      absoluteUrl: stub.dicomUrl,
      studyInstanceUID: ctx.studyUID,
      sopInstanceUID: task.inst.sopInstanceUID,
      fetchHeaders: authHeaders,
      skipIdbWrite: !!skipIdbWrite,
      skipIdbRead: !!skipIdbRead,
    })
    assertNotCancelled()
    const filled = applyStudyMetaToItem(
      {
        ...stub,
        image: loaded.image,
        imageId: loaded.imageId || stub.imageId,
        sopInstanceUID: loaded.sopInstanceUID || stub.sopInstanceUID,
        skeleton: false,
        fromCache: loaded.fromCache,
      },
      studyMetaFromDicomImage(loaded.image)
    )
    orderedItems[slot] = filled
    loadedSops[String(task.inst.sopInstanceUID)] = filled
    if (
      isPlaceholderPatientName(studyMeta.patientName) &&
      filled.patient &&
      !isPlaceholderPatientName(filled.patient.patientName)
    ) {
      Object.assign(
        studyMeta,
        mergeStudyMeta(studyMeta, {
          patientName: filled.patient.patientName,
          patientId: filled.patient.patientId,
          studyDate: filled.study && filled.study.studyDate,
          studyTime: filled.study && filled.study.studyTime,
          studyDescription: filled.study && filled.study.studyDescription,
        })
      )
      applyStudyMetaToItems(orderedItems, studyMeta)
    }
    return {
      ok: true,
      bytes: estimateLoadedBytes(loaded.image, loaded.arrayBuffer || loaded.blob),
    }
  }

  const loadPromises = Object.create(null)
  function loadSop(slot, stub, task) {
    const sop = String(task.inst.sopInstanceUID)
    if (loadedSops[sop] && loadedSops[sop].image) {
      return Promise.resolve({ ok: true, bytes: 0 })
    }
    if (loadPromises[sop]) return loadPromises[sop]
    loadPromises[sop] = fillLoadedItem(slot, stub, task).finally(() => {
      delete loadPromises[sop]
    })
    return loadPromises[sop]
  }

  function appendSeriesThumb(series, inst) {
    const uid = String(series.seriesInstanceUID)
    const existing = orderedItems.findIndex(
      (it) => it && it.seriesInstanceUID === uid
    )
    if (existing >= 0) return existing
    const hintTotal =
      series.relatedInstances > 0 ? series.relatedInstances : 1
    const item = buildSkeletonItem({
      studyUID: ctx.studyUID,
      series: { ...series, instances: [inst] },
      inst,
      slot: orderedItems.length,
      wadoUrl: ctx.wadoUrl,
      totalImagesInSeries: hintTotal,
      studyMeta,
    })
    orderedItems.push(item)
    setFilesStore([...orderedItems])
    onExplorerUpdate(buildExplorerData(orderedItems))
    return orderedItems.length - 1
  }

  const firstPaintPromise = (async () => {
    try {
      assertNotCancelled()
      const firstTask = {
        series: firstSeriesPaint,
        seriesIdx: 0,
        inst: firstGot.instances[0],
        instIdx: 0,
        total: firstHintTotal,
      }
      const paintResult = await loadSop(0, firstItem, firstTask)
      noteDownloadProgress(
        firstGot.instances[0] && firstGot.instances[0].sopInstanceUID,
        0,
        paintResult
      )
      setFilesStore([...orderedItems])
      onExplorerUpdate(buildExplorerData(orderedItems))
    } catch (ePaint) {
      if (ePaint && ePaint.name === 'AbortError') throw ePaint
      console.warn('[wadoUriLoader] first paint falló:', ePaint)
    }
  })()

  const thumbsQidoPromise = (async () => {
    const others = listed.seriesList.slice(1)
    await mapPool(others, 4, async (series) => {
      if (cancelled()) return
      try {
        const got = await fetchQidoSeriesInstances(
          qidoCtx,
          series.seriesInstanceUID,
          { limit: 1 }
        )
        if (!got.instances.length) return
        series.instances = got.instances
        if (got.qidoStudyMeta) {
          studyMeta = mergeStudyMeta(studyMeta, got.qidoStudyMeta)
        }
        const slot = appendSeriesThumb(series, got.instances[0])
        const stub = orderedItems[slot]
        const seriesIdx = listed.seriesList.indexOf(series)
        void loadSop(slot, stub, {
          series: { ...series, instances: got.instances },
          seriesIdx: seriesIdx >= 0 ? seriesIdx : slot,
          inst: got.instances[0],
          instIdx: 0,
          total: series.relatedInstances || 1,
        })
          .then((thumbResult) => {
            noteDownloadProgress(
              got.instances[0] && got.instances[0].sopInstanceUID,
              seriesIdx >= 0 ? seriesIdx : 0,
              thumbResult
            )
            setFilesStore([...orderedItems])
            onExplorerUpdate(buildExplorerData(orderedItems))
          })
          .catch((eWado) => {
            if (eWado && eWado.name === 'AbortError') return
            console.warn('[wadoUriLoader] thumb WADO:', series.seriesInstanceUID, eWado)
          })
      } catch (eThumb) {
        if (eThumb && eThumb.name === 'AbortError') throw eThumb
        console.warn('[wadoUriLoader] thumb QIDO serie falló:', series.seriesInstanceUID, eThumb)
      }
    })
  })()

  const firstPagePromise = (async () => {
    try {
      const got = await fetchQidoSeriesInstances(
        qidoCtx,
        firstSeries.seriesInstanceUID,
        { limit: FIRST_SERIES_QIDO_PAGE }
      )
      if (got.instances.length) {
        firstSeries.instances = mergeQidoInstances(
          firstGot.instances,
          got.instances
        )
      } else if (!firstSeries.instances || !firstSeries.instances.length) {
        firstSeries.instances = firstGot.instances
      }
      if (got.qidoStudyMeta) {
        studyMeta = mergeStudyMeta(studyMeta, got.qidoStudyMeta)
      }
      syncSeriesInfoTotals()
    } catch (ePage) {
      if (!firstSeries.instances || !firstSeries.instances.length) {
        firstSeries.instances = firstGot.instances
      }
    }
    return firstSeries
  })()

  const firstSeriesFullPromise = (async () => {
    await firstPagePromise
    const have = (firstSeries.instances || []).length
    const hint = firstSeries.relatedInstances || 0
    if (have > 0 && ((hint > 0 && have >= hint) || have < FIRST_SERIES_QIDO_PAGE)) {
      return firstSeries
    }
    try {
      const got = await fetchQidoSeriesInstances(
        qidoCtx,
        firstSeries.seriesInstanceUID
      )
      if (got.instances.length) {
        firstSeries.instances = mergeQidoInstances(
          firstSeries.instances,
          got.instances
        )
      }
      if (got.qidoStudyMeta) {
        studyMeta = mergeStudyMeta(studyMeta, got.qidoStudyMeta)
      }
      syncSeriesInfoTotals()
    } catch (eFull) {
      if (!firstSeries.instances || !firstSeries.instances.length) {
        firstSeries.instances = firstGot.instances
      }
    }
    return firstSeries
  })()

  const restOthersPromise = (async () => {
    const others = listed.seriesList.slice(1)
    await mapPool(others, 2, async (series) => {
      if (cancelled()) return
      const got = await fetchQidoSeriesInstances(qidoCtx, series.seriesInstanceUID)
      if (got.instances.length) series.instances = got.instances
      if (got.qidoStudyMeta) {
        studyMeta = mergeStudyMeta(studyMeta, got.qidoStudyMeta)
      }
      syncSeriesInfoTotals()
    })
    return others
  })()

  const [studyQidoMeta] = await Promise.all([
    studyMetaPromise,
    firstPaintPromise,
    firstPagePromise,
  ])

  const structure = listed.seriesList.filter(
    (s) => s.instances && s.instances.length > 0
  )

  if (!structure.length) {
    throw new Error('QIDO-RS no devolvió series con instancias')
  }

  studyMeta = mergeStudyMeta(studyMeta, studyQidoMeta)

  const tasks = []
  structure.forEach((series, seriesIdx) => {
    const total = series.instances.length
    series.instances.forEach((inst, instIdx) => {
      tasks.push({ series, seriesIdx, inst, instIdx, total })
    })
  })

  const numSeries = structure.length
  const seriesSlots = buildSeriesSlots(tasks)
  const nextInfo = structure.map((series, idx) => ({
    seriesNumber: parseInt(series.seriesNumber, 10) || idx + 1,
    description: String(
      series.description != null ? series.description : ''
    ).trim(),
    totalTasks: (seriesSlots.get(idx) || []).length,
  }))
  seriesInfo.length = 0
  nextInfo.forEach((row) => seriesInfo.push(row))
  progressTracker.growTotalTasks(tasks.length)
  progressTracker.setExpectedTotal(Math.max(expectedHint, tasks.length))

  const rebuilt = tasks.map((task, slot) => {
    const stub = buildSkeletonItem({
      studyUID: ctx.studyUID,
      series: task.series,
      inst: task.inst,
      slot,
      wadoUrl: ctx.wadoUrl,
      totalImagesInSeries: task.total,
      studyMeta,
    })
    const prev = loadedSops[String(task.inst.sopInstanceUID)]
    if (prev && prev.image) {
      return {
        ...stub,
        image: prev.image,
        imageId: prev.imageId || stub.imageId,
        skeleton: false,
        fromCache: prev.fromCache,
        patient: prev.patient || stub.patient,
        study: prev.study || stub.study,
      }
    }
    return stub
  })
  const knownSopsAtRebuild = Object.create(null)
  rebuilt.forEach((it) => {
    if (it && it.sopInstanceUID) knownSopsAtRebuild[String(it.sopInstanceUID)] = true
  })
  const thumbKeep = orderedItems.filter(
    (it) =>
      it &&
      it.sopInstanceUID &&
      !knownSopsAtRebuild[String(it.sopInstanceUID)]
  )
  orderedItems = rebuilt.concat(thumbKeep)

  const parallel = Math.max(
    1,
    concurrency > 0 ? concurrency : getManifestDownloadConcurrency(tasks.length)
  )

  const scheduler = createSeriesPriorityScheduler({
    numSeries,
    seriesSlots,
    seriesInfo,
    parallel,
    getActiveSeriesIdx:
      typeof getActiveSeriesIdx === 'function' ? getActiveSeriesIdx : () => 0,
    getPinnedSeriesIdx:
      typeof getPinnedSeriesIdx === 'function' ? getPinnedSeriesIdx : () => [],
  })

  setFilesStore([...orderedItems])
  onExplorerUpdate(buildExplorerData(orderedItems))
  isOpenStore({ index: activeDcmIndex, value: true })
  progressTracker.emit(true)

  async function worker() {
    while (!scheduler.isDone() && scheduler.completed.size < tasks.length) {
      if (cancelled()) return
      const mySlot = scheduler.pickNextSlot()
      if (mySlot == null) {
        if (scheduler.completed.size >= tasks.length) return
        await new Promise((r) => setTimeout(r, 2))
        continue
      }

      const task = tasks[mySlot]
      const stub = orderedItems[mySlot]
      if (stub && stub.image && stub.skeleton === false) {
        scheduler.finishSlot(mySlot)
        noteDownloadProgress(task.inst && task.inst.sopInstanceUID, task.seriesIdx, {
          ok: true,
          bytes: 0,
        })
        continue
      }
      const absoluteUrl = stub.dicomUrl
      let ok = false
      let bytes = 0
      let aborted = false
      let explorerNeedsName = false

      try {
        assertNotCancelled()
        const result = await loadSop(mySlot, stub, task)
        ok = !!(result && result.ok)
        bytes = (result && result.bytes) || 0
        const filled = orderedItems[mySlot]
        if (
          isPlaceholderPatientName(studyMeta.patientName) &&
          filled &&
          filled.patient &&
          !isPlaceholderPatientName(filled.patient.patientName)
        ) {
          explorerNeedsName = true
        }
      } catch (e) {
        if (e && e.name === 'AbortError') {
          aborted = true
        } else {
          console.warn('[wadoUriLoader] instancia falló:', absoluteUrl, e)
          orderedItems[mySlot] = {
            ...stub,
            error: (e && e.message) || String(e),
            skeleton: true,
          }
        }
      } finally {
        scheduler.finishSlot(mySlot)
      }

      if (aborted || cancelled()) return

      noteDownloadProgress(task.inst && task.inst.sopInstanceUID, task.seriesIdx, {
        ok,
        bytes,
      })
      const { totalDone } = progressTracker.totals
      setFilesStore([...orderedItems])
      if (explorerNeedsName || totalDone % 3 === 0 || totalDone === tasks.length) {
        onExplorerUpdate(buildExplorerData(orderedItems))
      }
      progressTracker.emit(false)
    }
  }

  await Promise.all(Array.from({ length: parallel }, () => worker()))

  if (cancelled()) {
    throw createStudyLoadAbortError()
  }

  try {
    await restOthersPromise
  } catch (eRest) {
    console.warn('[wadoUriLoader] QIDO resto series:', eRest)
  }

  const knownSops = Object.create(null)
  orderedItems.forEach((it) => {
    if (it && it.sopInstanceUID) knownSops[String(it.sopInstanceUID)] = true
  })
  const extraTasks = []
  listed.seriesList.forEach((series) => {
    const insts = (series && series.instances) || []
    insts.forEach((inst) => {
      if (!inst || !inst.sopInstanceUID) return
      if (knownSops[String(inst.sopInstanceUID)]) return
      extraTasks.push({
        series,
        seriesIdx: resolveWadoExtraSeriesIdx(series, structure, listed.seriesList),
        inst,
        instIdx: extraTasks.length,
        total: insts.length,
      })
    })
  })
  if (extraTasks.length && !cancelled()) {
    const extraBySeries = new Map()
    extraTasks.forEach((task) => {
      extraBySeries.set(
        task.seriesIdx,
        (extraBySeries.get(task.seriesIdx) || 0) + 1
      )
    })
    extraBySeries.forEach((n, idx) => {
      if (!seriesInfo[idx]) {
        const match = extraTasks.find((t) => t.seriesIdx === idx)
        const s = (match && match.series) || listed.seriesList[idx]
        seriesInfo[idx] = {
          seriesNumber: (s && parseInt(s.seriesNumber, 10)) || idx + 1,
          description: String((s && s.description) || '').trim(),
          totalTasks: 0,
        }
      }
      seriesInfo[idx].totalTasks = (seriesInfo[idx].totalTasks || 0) + n
    })
    const base = orderedItems.length
    extraTasks.forEach((task, i) => {
      orderedItems.push(
        buildSkeletonItem({
          studyUID: ctx.studyUID,
          series: task.series,
          inst: task.inst,
          slot: base + i,
          wadoUrl: ctx.wadoUrl,
          totalImagesInSeries: task.total,
          studyMeta,
        })
      )
    })
    setFilesStore([...orderedItems])
    onExplorerUpdate(buildExplorerData(orderedItems))
    progressTracker.growTotalTasks(orderedItems.length)
    progressTracker.setExpectedTotal(Math.max(expectedHint, orderedItems.length))
    progressTracker.emit(true)
    await runPriorityWadoPool({
      tasks: extraTasks,
      seriesInfo,
      parallel,
      getActiveSeriesIdx,
      getPinnedSeriesIdx,
      cancelled,
      onSlot: async (slot, task) => {
        if (cancelled()) return
        let extraResult = { ok: false, bytes: 0 }
        try {
          extraResult =
            (await loadSop(base + slot, orderedItems[base + slot], task)) ||
            extraResult
        } catch (eEx) {
          if (eEx && eEx.name === 'AbortError') throw eEx
        }
        noteDownloadProgress(
          task.inst && task.inst.sopInstanceUID,
          task.seriesIdx,
          extraResult
        )
        if (slot % 3 === 0) {
          setFilesStore([...orderedItems])
        }
      },
    })
    setFilesStore([...orderedItems])
    onExplorerUpdate(buildExplorerData(orderedItems))
  }

  progressTracker.setCatalogComplete(true, orderedItems.length)
  progressTracker.emit(true)
  onExplorerUpdate(buildExplorerData(orderedItems))

  const okItems = orderedItems.filter((it) => it.imageId && !it.error)
  if (okItems.length === 0) {
    throw new Error('No se pudo cargar ninguna instancia via WADO-URI')
  }

  return {
    instanceCount: orderedItems.length,
    loadedCount: progressTracker.totals.loadedOk,
    items: orderedItems.map((it) => ({ ...it, image: it.image || null })),
    structure,
  }
}

/**
 * Re-descubre estructura QIDO (p. ej. tras STOW de GSPS/SR).
 * @param {object} ctx
 * @returns {Promise<Array>}
 */
export async function refreshLegacyWadoStructure(ctx) {
  return discoverStudyStructure(ctx)
}

export { fallbackCacheKey }
